import * as React from 'react';

import { formsApi } from '../api/formsApi';

import type { FieldDTO, OptionDTO } from '../api/formsApi';

/**
 * ✅ OTIMIZAÇÃO IMPLEMENTADA: Campo extras
 *
 * O backend agora retorna nas condições um campo "extras" que contém:
 * - form_uuid: UUID do formulário
 * - field_uuid: UUID do campo
 * - option_id / option_ids: UUID(s) da(s) opção(ões)
 * - field_label: Label do campo (para exibição)
 *
 * Isso permite:
 * ✅ Hidratação direta usando field_uuid do extras
 * ✅ Evitar mapeamento optionId -> fieldId
 * ✅ Reduzir requisições desnecessárias
 * ✅ Carregar apenas opções do field_uuid específico
 *
 * A função hydrateConditionFromExtras() usa esses dados diretamente.
 */

// Cache global para evitar múltiplas chamadas
const globalOptionCache: Record<string, OptionDTO[]> = {};
const globalOptionToFieldCache: Record<string, string> = {};

export function useOptimizedHydration() {
  const [loading, setLoading] = React.useState(false);

  // Função para hidratação batch - processa todas as condições de uma vez
  const hydrateAllConditionsBatch = React.useCallback(
    async (
      conditions: Array<{ rightOperand: string; nodeId: string }>
    ): Promise<Record<string, { field: FieldDTO | null; options: OptionDTO[] }>> => {
      console.log(`🔄 === INÍCIO HIDRATAÇÃO BATCH ===`);
      console.log(`📋 Processando ${conditions.length} condições simultaneamente`);

      const results: Record<string, { field: FieldDTO | null; options: OptionDTO[] }> = {};

      try {
        setLoading(true);

        // 1. Buscar todos os campos de uma vez
        console.log(`📥 Carregando todos os campos...`);
        const fields = await formsApi.getAllFields();
        console.log(`✅ ${fields.length} campos carregados`);

        // 2. Processar cada condição sequencialmente para evitar travamento
        console.log(`🔄 Processando ${conditions.length} condições sequencialmente...`);

        const optionToFieldMap: Record<string, FieldDTO | null> = {};

        for (const condition of conditions) {
          const optionId = condition.rightOperand;
          console.log(`🔍 Processando condição ${condition.nodeId} com opção: ${optionId}`);

          // Verificar cache primeiro
          if (globalOptionToFieldCache[optionId]) {
            const cachedFieldId = globalOptionToFieldCache[optionId];
            const field = fields.find(f => f.id === cachedFieldId);
            if (field) {
              console.log(`⚡ Campo encontrado no cache para opção ${optionId}: ${field.label}`);
              optionToFieldMap[optionId] = field;
              continue;
            }
          }

          // Buscar campo para esta opção específica
          let fieldFound = false;
          for (const field of fields) {
            try {
              // Verificar se já temos as opções em cache
              let options = globalOptionCache[field.id];

              if (!options) {
                console.log(`📥 Carregando opções para campo: ${field.label}`);
                options = await formsApi.getFieldOptions(field.id);
                globalOptionCache[field.id] = options;
              }

              const option = options.find(opt => opt.id === optionId);
              if (option) {
                console.log(`🎯 Campo encontrado para opção ${optionId}:`, {
                  fieldId: field.id,
                  fieldLabel: field.label,
                  optionId: option.id,
                  optionLabel: option.option_label,
                  totalOptions: options.length,
                });
                globalOptionToFieldCache[optionId] = field.id;
                optionToFieldMap[optionId] = field;
                fieldFound = true;
                break; // Parar de buscar quando encontrar
              }
            } catch (error) {
              console.error(`Erro ao processar campo ${field.id}:`, error);
            }
          }

          if (!fieldFound) {
            console.log(
              `❌ Campo não encontrado para opção ${optionId} após verificar ${fields.length} campos`
            );
            optionToFieldMap[optionId] = null;
          }
        }

        // 3. Para cada condição, montar o resultado
        console.log(`🔧 Montando resultados finais para ${conditions.length} condições...`);
        for (const condition of conditions) {
          const field = optionToFieldMap[condition.rightOperand];
          const options = field ? globalOptionCache[field.id] || [] : [];

          results[condition.nodeId] = { field, options };

          if (field) {
            console.log(`✅ Condição ${condition.nodeId} hidratada:`, {
              fieldId: field.id,
              fieldLabel: field.label,
              optionsCount: options.length,
              rightOperand: condition.rightOperand,
            });
          } else {
            console.log(`❌ Condição ${condition.nodeId} não hidratada:`, {
              rightOperand: condition.rightOperand,
              fieldFound: !!field,
            });
          }
        }

        console.log(`✅ === FIM HIDRATAÇÃO BATCH ===`);
        return results;
      } catch (error) {
        console.error('Erro na hidratação batch:', error);
        return results;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Função para obter opções de um campo (com cache)
  const getFieldOptionsOptimized = React.useCallback(
    async (fieldId: string): Promise<OptionDTO[]> => {
      if (globalOptionCache[fieldId]) {
        console.log(`⚡ Opções já em cache para campo: ${fieldId}`);
        return globalOptionCache[fieldId];
      }

      try {
        console.log(`📥 Carregando opções para campo: ${fieldId}`);
        const options = await formsApi.getFieldOptions(fieldId);

        globalOptionCache[fieldId] = options;
        return options;
      } catch (error) {
        console.error(`Erro ao carregar opções do campo ${fieldId}:`, error);
        return [];
      }
    },
    []
  );

  // Função para hidratar uma condição específica (usando batch internamente)
  const hydrateCondition = React.useCallback(
    async (rightOperand: string): Promise<{ field: FieldDTO | null; options: OptionDTO[] }> => {
      if (!rightOperand || typeof rightOperand !== 'string') {
        return { field: null, options: [] };
      }

      console.log(`🔄 Hidratando condição individual com right_operand: ${rightOperand}`);

      // Usar a função batch para uma única condição
      const results = await hydrateAllConditionsBatch([
        {
          rightOperand,
          nodeId: 'single-condition',
        },
      ]);

      const result = results['single-condition'];
      if (result?.field) {
        console.log(`✅ Hidratação individual completa para campo: ${result.field.label}`);
        return result;
      }

      console.log(`❌ Hidratação individual falhou para opção: ${rightOperand}`);
      return { field: null, options: [] };
    },
    [hydrateAllConditionsBatch]
  );

  /**
   * ✅ NOVA FUNÇÃO: Hidratação otimizada usando extras
   * Se a condição vem com extras (field_uuid), usa diretamente para carregar opções
   * Evita buscar todos os campos e fazer mapeamento option_id → field_id
   */
  const hydrateConditionFromExtras = React.useCallback(
    async (
      extras?: Record<string, any>
    ): Promise<{ field: FieldDTO | null; options: OptionDTO[] }> => {
      if (!extras || !extras.field_uuid) {
        console.log('⚠️ Sem extras.field_uuid, usando hidratação normal');
        return { field: null, options: [] };
      }

      console.log('⚡ Hidratação otimizada usando extras.field_uuid:', extras.field_uuid);

      try {
        // Verificar se já temos as opções em cache
        if (globalOptionCache[extras.field_uuid]) {
          console.log('⚡ Opções já em cache, retornando imediatamente');

          // Buscar o campo nos fields já carregados ou criar um mock
          const allFields = await formsApi.getAllFields();
          const field = allFields.find(f => f.id === extras.field_uuid) || null;

          return {
            field,
            options: globalOptionCache[extras.field_uuid],
          };
        }

        // Carregar opções diretamente usando field_uuid
        console.log('📥 Carregando opções para field_uuid:', extras.field_uuid);
        const options = await formsApi.getFieldOptions(extras.field_uuid);

        // Salvar no cache
        globalOptionCache[extras.field_uuid] = options;

        // Buscar informações do campo
        const allFields = await formsApi.getAllFields();
        const field = allFields.find(f => f.id === extras.field_uuid) || null;

        console.log('✅ Hidratação otimizada completa:', {
          field: field?.label,
          optionsCount: options.length,
        });

        return { field, options };
      } catch (error) {
        console.error('❌ Erro na hidratação otimizada:', error);
        return { field: null, options: [] };
      }
    },
    []
  );

  return {
    loading,
    hydrateAllConditionsBatch,
    hydrateCondition,
    getFieldOptionsOptimized,
    hydrateConditionFromExtras, // ✅ Nova função otimizada
  };
}
